'use client'

import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { StaffTransaction, StaffTransactionInput, StaffTransactionType } from '@garage/shared'
import {
  staffTransactionInputSchema,
  STAFF_TRANSACTION_TYPES,
  STAFF_TRANSACTION_TYPE_LABELS,
} from '@garage/shared'
import { Dialog, TextField, Segmented, Checkbox, Button, useSnackbar } from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

interface StaffTransactionDialogProps {
  open: boolean
  workerId: string
  transaction?: StaffTransaction
  onClose: () => void
}

interface FormState {
  type: StaffTransactionType
  amountStr: string
  reason: string
  occurredAt: string
  notes: string | null
  settled: boolean
}

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

export function StaffTransactionDialog({ open, workerId, transaction, onClose }: StaffTransactionDialogProps) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const isCreate = !transaction

  const initialState = (): FormState => ({
    type: transaction?.type ?? 'advance',
    amountStr: transaction ? String(transaction.amountMinor / 100) : '',
    reason: transaction?.reason ?? '',
    occurredAt: transaction?.occurredAt.slice(0, 10) ?? today(),
    notes: transaction?.notes ?? null,
    settled: transaction ? transaction.settledAt !== null : false,
  })

  const [formState, setFormState] = useState<FormState>(initialState)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (open) {
      setFormState(initialState())
      setFieldErrors({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction])

  const mutation = useMutation({
    mutationFn: async (input: StaffTransactionInput) =>
      isCreate
        ? api.workers.addTransaction(workerId, input)
        : api.workers.updateTransaction(workerId, transaction!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.worker(workerId) })
      queryClient.invalidateQueries({ queryKey: qk.workers() })
      snackbar.success(isCreate ? t('staffTransactionDialog.recordAdded') : t('staffTransactionDialog.recordUpdated'))
      onClose()
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors)
        snackbar.error(error.message)
      } else {
        snackbar.error(t('common.errorOccurred'))
      }
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const amountMinor = Math.round(parseFloat(formState.amountStr || '0') * 100)

    // Client-side validation: amount can't go below paidMinor for records with payments
    if (transaction && transaction.paidMinor > 0 && amountMinor < transaction.paidMinor) {
      setFieldErrors({
        amountMinor: t('staffTransactionDialog.cannotGoBelow', {
          amount: transaction.paidMinor / 100,
        }),
      })
      return
    }

    const input: StaffTransactionInput = {
      type: formState.type,
      amountMinor,
      reason: formState.reason,
      occurredAt: formState.occurredAt,
      notes: formState.notes,
      settled: formState.settled,
    }

    const result = staffTransactionInputSchema.safeParse(input)
    if (!result.success) {
      const errors: Record<string, string> = {}
      result.error.issues.forEach((issue) => {
        errors[issue.path.join('.')] = issue.message
      })
      setFieldErrors(errors)
      return
    }

    setFieldErrors({})
    mutation.mutate(input)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('workerDetail.addRecord') : t('workerDetail.editRecord')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Segmented
          label={t('workerDetail.whatHappened')}
          options={STAFF_TRANSACTION_TYPES.filter((type) => type !== 'overtime' || transaction?.type === 'overtime').map((type) => ({
            value: type,
            label: t(`workerDetail.transactionTypes.${type}`, STAFF_TRANSACTION_TYPE_LABELS[type]),
          }))}
          value={formState.type}
          onChange={(type) => setFormState((s) => ({ ...s, type: type as StaffTransactionType }))}
        />

        <TextField
          label={t('workerDetail.amount')}
          value={formState.amountStr}
          onChange={(v) => setFormState((s) => ({ ...s, amountStr: v }))}
          error={fieldErrors.amountMinor}
          numeric
          fullWidth
          supportingText={
            transaction && transaction.paidMinor > 0
              ? t('staffTransactionDialog.alreadyPaid', {
                  amount: transaction.paidMinor / 100,
                })
              : undefined
          }
        />

        <TextField
          label={t('workerDetail.whatFor')}
          value={formState.reason}
          onChange={(v) => setFormState((s) => ({ ...s, reason: v }))}
          error={fieldErrors.reason}
          placeholder={t('staffTransactionDialog.reasonPlaceholder')}
          fullWidth
        />

        <TextField
          label={t('calendar.date')}
          type="date"
          value={formState.occurredAt}
          onChange={(v) => setFormState((s) => ({ ...s, occurredAt: v }))}
          error={fieldErrors.occurredAt}
          fullWidth
        />

        <TextField
          label={t('clientDetail.notes')}
          value={formState.notes ?? ''}
          onChange={(v) => setFormState((s) => ({ ...s, notes: v || null }))}
          error={fieldErrors.notes}
          multiline
          rows={2}
          fullWidth
        />

        {!transaction || transaction.payments.length === 0 ? (
          <Checkbox
            checked={formState.settled}
            onChange={(checked) => setFormState((s) => ({ ...s, settled: checked }))}
            label={t('staffTransactionDialog.alreadySettled')}
            supportingText={t('staffTransactionDialog.alreadySettledHelp')}
          />
        ) : null}
      </form>
    </Dialog>
  )
}
